"use client"

import { Check, ChevronsUpDown, Search, X } from "lucide-react"
import * as React from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export const title = "No Results Variation"

export type SearchableComboboxOption = {
  value: string
  label: string
  /** Optional icon displayed next to the label. */
  icon?: LucideIcon
  /** Text used by cmdk for filtering (defaults to `label` + space + `value`). */
  filterText?: string
  /** When both are set, list/trigger render `flag` + `value` + `name` (currency row). */
  flag?: string
  name?: string
}

function isCurrencyStyleOption(opt: SearchableComboboxOption): boolean {
  return opt.flag != null && opt.name != null && opt.value.length === 3
}

function defaultOptionBody(opt: SearchableComboboxOption): React.ReactNode {
  if (isCurrencyStyleOption(opt)) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="shrink-0 text-base leading-none" aria-hidden>
          {opt.flag}
        </span>
        <span className="min-w-0 truncate">
          <span className="font-medium tabular-nums">{opt.value}</span>
          <span className="text-muted-foreground"> — {opt.name}</span>
        </span>
      </span>
    )
  }
  return (
    <>
      {opt.icon ? (
        <opt.icon className="mr-2 size-4 shrink-0 text-muted-foreground" aria-hidden />
      ) : null}
      <span className="min-w-0 truncate">{opt.label}</span>
    </>
  )
}

type ComboboxCommandProps = {
  options: SearchableComboboxOption[]
  value: string
  onValueChange: (value: string) => void
  searchPlaceholder: string
  renderOption?: (option: SearchableComboboxOption) => React.ReactNode
  commandListClassName?: string
  /** e.g. close popover after pick */
  afterSelect?: () => void
}

function SearchableComboboxCommand({
  options,
  value,
  onValueChange,
  searchPlaceholder,
  renderOption,
  commandListClassName,
  afterSelect,
}: ComboboxCommandProps) {
  return (
    <>
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList className={cn("max-h-56", commandListClassName)}>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <Search className="size-8 text-muted-foreground" aria-hidden />
            <div>
              <p className="text-sm font-medium">No results</p>
              <p className="text-xs text-muted-foreground">Try a different search term</p>
            </div>
          </div>
        </CommandEmpty>
        <CommandGroup>
          {options.map((opt) => {
            const filterValue = opt.filterText ?? `${opt.label} ${opt.value}`.trim()
            const kw = isCurrencyStyleOption(opt)
              ? [opt.value, opt.name ?? "", opt.label]
              : [opt.value, opt.label]
            return (
              <CommandItem
                key={opt.value}
                className="[&>svg:last-child]:hidden"
                value={filterValue}
                keywords={kw}
                onSelect={() => {
                  onValueChange(opt.value)
                  afterSelect?.()
                }}
              >
                <Check
                  className={cn(
                    "mr-2 size-4 shrink-0",
                    value === opt.value ? "opacity-100" : "opacity-0",
                  )}
                  aria-hidden
                />
                {renderOption ? renderOption(opt) : defaultOptionBody(opt)}
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </>
  )
}

export type SearchableComboboxProps = {
  value: string
  onValueChange: (value: string) => void
  options: SearchableComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  id?: string
  className?: string
  triggerClassName?: string
  contentClassName?: string
  /** Fires when the popover closes (Escape, outside click, or after picking an item). */
  onPopoverClosed?: () => void
  /** Show a clear control when `value` is non-empty. */
  clearable?: boolean
  /** Overrides default option row content (after the checkmark). */
  renderOption?: (option: SearchableComboboxOption) => React.ReactNode
  /**
   * `popover` — trigger + floating panel (default).
   * `inline` — search + list only, for nested menus (no nested Popover portal).
   */
  variant?: "popover" | "inline"
  /** Passed to CommandList when `variant` is `inline` (or overrides popover list height). */
  commandListClassName?: string
}

export function SearchableCombobox({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled,
  id,
  className,
  triggerClassName,
  contentClassName,
  clearable,
  renderOption,
  variant = "popover",
  commandListClassName,
  onPopoverClosed,
}: SearchableComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const selected = React.useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  )

  const triggerBody = React.useMemo(() => {
    if (!selected) return null
    if (renderOption) return renderOption(selected)
    return defaultOptionBody(selected)
  }, [renderOption, selected])

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next)
      if (!next) onPopoverClosed?.()
    },
    [onPopoverClosed],
  )

  if (variant === "inline") {
    return (
      <Command
        className={cn(
          "overflow-hidden rounded-lg bg-transparent text-sm shadow-none",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        <SearchableComboboxCommand
          options={options}
          value={value}
          onValueChange={onValueChange}
          searchPlaceholder={searchPlaceholder}
          renderOption={renderOption}
          commandListClassName={commandListClassName}
        />
      </Command>
    )
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <div
        className={cn(
          "relative flex h-8 w-full min-w-0 items-stretch overflow-hidden rounded-lg border border-input bg-background text-sm outline-none focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            aria-expanded={open}
            className={cn(
              "min-w-0 flex-1 justify-between gap-2 rounded-none border-0 bg-transparent px-3 font-normal shadow-none hover:bg-transparent",
              triggerClassName,
            )}
            disabled={disabled}
            role="combobox"
            variant="ghost"
          >
            <span
              className={cn(
                "flex min-w-0 flex-1 items-center text-left",
                !selected && "text-muted-foreground",
              )}
            >
              {selected ? triggerBody : placeholder}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        {clearable && value && !disabled ? (
          <button
            type="button"
            tabIndex={-1}
            className="flex shrink-0 items-center border-l border-border px-2.5 text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
            aria-label="Clear"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={() => {
              onValueChange("")
              onPopoverClosed?.()
            }}
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null}
        <PopoverContent
          align="start"
          className={cn(
            "w-[var(--radix-popover-trigger-width)] min-w-[12rem] max-w-[min(100vw-2rem,28rem)] p-0",
            contentClassName,
          )}
        >
          <Command>
            <SearchableComboboxCommand
              options={options}
              value={value}
              onValueChange={onValueChange}
              searchPlaceholder={searchPlaceholder}
              renderOption={renderOption}
              commandListClassName={commandListClassName}
              afterSelect={() => setOpen(false)}
            />
          </Command>
        </PopoverContent>
      </div>
    </Popover>
  )
}

const demoFruits = ["Apple", "Banana", "Cherry", "Date", "Elderberry"]

const Example = () => {
  const [value, setValue] = React.useState("")

  return (
    <div className="w-[200px]">
      <SearchableCombobox
        value={value}
        onValueChange={setValue}
        options={demoFruits.map((f) => ({ value: f, label: f }))}
        placeholder="Select fruit…"
        searchPlaceholder="Search fruits…"
        contentClassName="w-[200px] min-w-[200px] max-w-[200px]"
      />
    </div>
  )
}

export default Example
